import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { trackEvent } from '@ari/analytics';
import './App.css';
// @ts-ignore - generated at build time (used as fallback)
import navigationConfig from './navigationData.json';

interface NavigationItem {
  title: string;
  description?: string;
  href?: string; // For external links
  image?: string; // Optional image/gif URL
  visible?: boolean;
  children?: NavigationItem[]; // For sub-pages
}

interface LandingSettings {
  pageTitle: string;
  headerTitle: string;
  subtitle: string;
  footerText: string;
}

interface BreadcrumbItem {
  title: string;
  item: NavigationItem;
}

interface ResolvedNavigation {
  breadcrumbs: BreadcrumbItem[];
  currentItems: NavigationItem[];
  currentImage?: string;
}

/**
 * Remove hidden navigation items and prune empty categories.
 */
function filterVisibleNavigation(items: NavigationItem[]): NavigationItem[] {
  return items
    .filter((item) => item.visible !== false)
    .map((item) => {
      const visibleChildren = item.children
        ? filterVisibleNavigation(item.children)
        : undefined;

      return {
        ...item,
        children: visibleChildren,
      };
    })
    .filter((item) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      const hasHref = Boolean(item.href);
      return hasChildren || hasHref;
    });
}

/**
 * Convert a title into a URL-safe slug segment.
 */
function slugifyTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .trim()
    .replace(/["']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || 'item';
}

/**
 * Build a path from a breadcrumb chain.
 */
function buildPathFromBreadcrumbs(breadcrumbs: BreadcrumbItem[]): string {
  if (breadcrumbs.length === 0) {
    return '/';
  }

  const segments = breadcrumbs.map(crumb => slugifyTitle(crumb.title));
  return `/${segments.join('/')}`;
}

/**
 * Resolve URL path segments into the current navigation level.
 */
function resolveNavigationState(
  rootNavigation: NavigationItem[],
  segments: string[]
): ResolvedNavigation | null {
  if (segments.length === 0) {
    return {
      breadcrumbs: [],
      currentItems: rootNavigation,
      currentImage: undefined,
    };
  }

  let currentItems = rootNavigation;
  const breadcrumbs: BreadcrumbItem[] = [];

  for (const segment of segments) {
    const match = currentItems.find(item => {
      const isFolder = Boolean(item.children && item.children.length > 0);
      return isFolder && slugifyTitle(item.title) === segment;
    });

    if (!match || !match.children) {
      return null;
    }

    breadcrumbs.push({
      title: match.title,
      item: match,
    });
    currentItems = match.children;
  }

  return {
    breadcrumbs,
    currentItems,
    currentImage: breadcrumbs[breadcrumbs.length - 1]?.item.image,
  };
}

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rootNavigation, setRootNavigation] = useState<NavigationItem[]>(
    navigationConfig as NavigationItem[]
  );
  const [settings, setSettings] = useState<LandingSettings>({
    pageTitle: 'ari.the.elk.wtf',
    headerTitle: "hi, i'm aria",
    subtitle: '✿',
    footerText: 'built with \u2665 in chicago',
  });

  // Fetch landing page settings from the API
  useEffect(() => {
    fetch('/api/landing/settings')
      .then(res => {
        if (!res.ok) throw new Error('API error');
        return res.json();
      })
      .then((data: LandingSettings) => {
        setSettings(data);
        if (data.pageTitle) {
          document.title = data.pageTitle;
        }
      })
      .catch(() => {
        // Keep defaults
      });
  }, []);

  // Fetch navigation from the API, falling back to the static JSON
  useEffect(() => {
    fetch('/api/landing/navigation')
      .then(res => {
        if (!res.ok) throw new Error('API error');
        return res.json();
      })
      .then((data: NavigationItem[]) => {
        if (Array.isArray(data) && data.length > 0) {
          setRootNavigation(data);
        }
      })
      .catch(() => {
        // Keep the statically-generated fallback
      });
  }, []);

  const pathSegments = useMemo(() => {
    return location.pathname
      .split('/')
      .filter(Boolean)
      .map((segment: string) => decodeURIComponent(segment));
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.startsWith('/no-missing-ids')) {
      return;
    }

    const redirectedPath = location.pathname.replace(/^\/no-missing-ids/, '') || '/';
    const redirectTarget = `https://nomissingids.com${redirectedPath}${location.search}`;

    trackEvent('no_missing_ids_redirect', {
      source_path: location.pathname,
      destination_path: redirectedPath,
      has_query: location.search.length > 0,
    });

    // Small delay lets the analytics event queue before navigating away.
    const timer = window.setTimeout(() => {
      window.location.replace(redirectTarget);
    }, 120);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.pathname, location.search]);

  const resolvedNavigation = useMemo(() => {
    const visibleRootNavigation = filterVisibleNavigation(rootNavigation);
    return resolveNavigationState(visibleRootNavigation, pathSegments);
  }, [rootNavigation, pathSegments]);

  useEffect(() => {
    if (!resolvedNavigation) {
      navigate('/', { replace: true });
    }
  }, [resolvedNavigation, navigate]);

  const breadcrumbs = resolvedNavigation?.breadcrumbs ?? [];
  const currentDisplayItems =
    resolvedNavigation?.currentItems ?? filterVisibleNavigation(rootNavigation);
  const currentImage = resolvedNavigation?.currentImage;
  const isAtRoot = breadcrumbs.length === 0;

  const navigateToSubPage = (item: NavigationItem) => {
    if (!item.children || item.children.length === 0) {
      return;
    }

    const nextBreadcrumbs = [...breadcrumbs, { title: item.title, item }];
    navigate(buildPathFromBreadcrumbs(nextBreadcrumbs));
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      navigate('/');
      return;
    }

    const nextBreadcrumbs = breadcrumbs.slice(0, index + 1);
    navigate(buildPathFromBreadcrumbs(nextBreadcrumbs));
  };

  const handleItemClick = (item: NavigationItem, e: React.MouseEvent) => {
    if (item.children && item.children.length > 0) {
      e.preventDefault();
      navigateToSubPage(item);
    }
    // If item has href but no children, let the default link behavior happen
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="container">
          <h1>{settings.headerTitle}</h1>
          <p className="subtitle">{settings.subtitle}</p>

          {/* Breadcrumb Navigation */}
          {!isAtRoot && (
            <div className="breadcrumbs">
              <button
                className="breadcrumb-item"
                onClick={() => navigateToBreadcrumb(-1)}
              >
                Home
              </button>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <span className="breadcrumb-separator">/</span>
                  <button
                    className="breadcrumb-item"
                    onClick={() => navigateToBreadcrumb(index)}
                  >
                    {crumb.title}
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Optional Image for Current Page */}
          {currentImage && (
            <div className="page-image">
              <img src={currentImage} alt="Page illustration" />
            </div>
          )}

          <div className="apps-grid">
            {currentDisplayItems.map((item, index) => (
              <a
                key={index}
                href={item.href || '#'}
                className="app-card"
                target={item.href?.startsWith('http') ? '_blank' : '_self'}
                rel={
                  item.href?.startsWith('http')
                    ? 'noopener noreferrer'
                    : undefined
                }
                onClick={e => handleItemClick(item, e)}
              >
                <div className="app-card-text">
                  <h3>{item.title}</h3>
                  {item.description && <p>{item.description}</p>}
                </div>
                <span className="app-card-arrow">
                  {item.children
                    ? '›'
                    : item.href?.startsWith('http')
                      ? '↗'
                      : '→'}
                </span>
              </a>
            ))}
          </div>

          <footer className="footer">
            <p>{settings.footerText}</p>
          </footer>
        </div>
      </header>
    </div>
  );
};

export default App;
